import { useState, useEffect, useRef } from "react";
import useSpeechSynthesis from "../../../hooks/useSpeechSynthesis";
import InputFactory from "../../../components/InputFactory";
import { Progress } from "../../../nq-component";
import VoiceConfiguration from "./VoiceConfiguration.js";

const getStoredVoiceName = (voiceName) => {
  if (!voiceName) return null;
  if (typeof voiceName === "string") return voiceName;
  if (typeof voiceName === "object" && voiceName.name) return voiceName.name;
  return null;
};

export default function SpeechSettings({
  setting,
  progress,
  submit,
  onChange,
}) {
  const { speak, pause, resume, cancel, voices, isPlaying, isPaused } =
    useSpeechSynthesis();

  const [text, setText] = useState(
    "LGS Staff Management Services Incorporated is a high caliber professional manpower service provider for Hotels & Restaurant. We are recognized for our quality and efficiency in providing you the manpower, just when you need it the most.",
  );

  const [selectedVoice, setSelectedVoice] = useState(null);
  const [currentRepeat, setCurrentRepeat] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [highlightedText, setHighlightedText] = useState("");
  const [settingData, setSetting] = useState({});
  const initializedDefaultVoice = useRef(false);

  useEffect(() => {
    setSetting({ ...setting });
  }, [setting]);

  useEffect(() => {
    if (voices.length === 0) return;

    const storedName = getStoredVoiceName(setting?.voice_name);

    if (!storedName) {
      const recommendedVoice = voices.find((v) => v.default) || voices[0];
      setSelectedVoice(recommendedVoice);
      if (!initializedDefaultVoice.current && recommendedVoice?.name) {
        initializedDefaultVoice.current = true;
        onChange(recommendedVoice.name, "voice_name");
      }
      return;
    }

    const matchedVoice =
      voices.find((voice) => voice.name === storedName) ||
      voices.find((voice) => voice.name.includes(storedName)) ||
      voices.find((v) => v.default) ||
      voices[0];

    setSelectedVoice(matchedVoice);
  }, [voices, setting?.voice_name, onChange]);

  const handleSpeak = async () => {
    if (!text.trim()) {
      alert("Please enter some text to speak");
      return;
    }

    const repetitionCount = settingData?.repetition_count ?? 1;
    const options = {
      voice_name: selectedVoice,
      speech_rate: settingData?.speech_rate,
      speech_pitch: settingData?.speech_pitch,
      speech_volume: settingData?.speech_volume,
      repetition_count: repetitionCount,
      repeatDelay: 500,
      onBoundary: (event) => {
        if (event.name === "word") {
          const word = text.substring(
            event.charIndex,
            event.charIndex + event.charLength,
          );

          const before = text.substring(0, event.charIndex);
          const current = word;
          const after = text.substring(event.charIndex + event.charLength);
          setHighlightedText({ before, current, after });
        }
      },
      onRepeat: (repeatNum) => {
        setCurrentRepeat(repeatNum);
      },
      onEnd: () => {
        setHighlightedText("");
        setCurrentRepeat(0);
      },
    };

    try {
      await speak(text, options);
    } catch (error) {
      console.error("Speech error:", error);
      alert("Error during speech synthesis");
    }
  };

  const handlePlayPause = () => {
    if (isPlaying && !isPaused) {
      pause();
    } else if (isPaused) {
      resume();
    } else {
      handleSpeak();
    }
  };

  const voiceOPtions = () => voices.map((voice) => voice.name);

  const _onChange = (field, value) => {
    setSetting((prev) => ({ ...prev, [field]: value }));

    if (field === "voice_name") {
      const voice = voices.find((v) => v.name === value);
      if (voice) {
        setSelectedVoice(voice);
      }
    }

    onChange(value, field);
  };

  const _submit = () => {
    submit(settingData);
  };

  const repetitionCount = settingData?.repetition_count ?? 1;

  const actionButtons = () => {
    return (
      <>
        <div className="d-flex justify-content-between flex-column flex-md-row gap-2 gap-md-0">
          <div className="d-flex flex-wrap gap-2">
            <button
              onClick={handlePlayPause}
              disabled={!text.trim()}
              style={{ backgroundColor: "#d18237" }}
              className="btn btn-md text-light fw-bold"
            >
              {isPlaying && !isPaused ? (
                <>
                  <i className="bi bi-pause-fill me-2"></i>
                  Pause
                </>
              ) : isPaused ? (
                <>
                  <i className="bi bi-play-fill me-2"></i>
                  Resume
                </>
              ) : (
                <>
                  <i className="bi bi-play-fill me-2"></i>
                  Play
                </>
              )}
            </button>

            <button
              onClick={cancel}
              disabled={!isPlaying}
              className="btn btn-danger btn-md"
            >
              <i className="bi bi-stop-fill me-2"></i>
              Stop
            </button>

            <button
              onClick={() => setShowSettings(!showSettings)}
              className="btn btn-dark"
            >
              <i className="bi bi-gear-fill me-2"></i>
              {showSettings ? "Hide" : "Show"} Settings
            </button>

            {isPlaying && (
              <div className="btn btn-success btn-md disabled">
                <span className="spinner-grow spinner-grow-sm me-2"></span>
                Speaking...{" "}
                {currentRepeat > 0 &&
                  `(${currentRepeat}/${repetitionCount})`}
              </div>
            )}
          </div>

          <button className="btn btn-success fw-bold" onClick={() => _submit()}>
            <i className="bi bi-check-circle me-2"></i>Apply Settings
          </button>
        </div>
      </>
    );
  };

  return (
    <>
      <div className="card shadow-sm border-0">
        {!progress ? (
          <div className="card-body p-4">
            <div className="mb-4">
              <label className="form-label fw-bold text-muted">
                <i className="bi bi-chat-text me-2 text-dark"></i>
                Text to Speak:
              </label>

              <InputFactory
                type="Text"
                onChange={(value) => setText(value)}
                placeholder="Enter a Text to Speak"
                value={text}
              />
            </div>

            <div className="mb-4">
              <label className="form-label fw-bold text-muted">
                <i className="bi bi-mic-fill me-2 text-dark"></i>
                Select Voice:
              </label>

              <InputFactory
                type="Enum"
                required
                options={voiceOPtions()}
                className="form-control bg-light-subtle p-2"
                placeholder="Select"
                field="voice_name"
                defaultValue={selectedVoice?.name || voices[0]?.name}
                onChange={(value, field) => _onChange(field, value)}
              />

              {selectedVoice && (
                <div
                  className="alert alert-light border border-info mt-3 mb-0"
                  style={{ backgroundColor: "#ecfcff" }}
                >
                  <div className="fw-bold mb-2">
                    <i
                      className="bi bi-info-circle-fill me-2 text"
                      style={{ color: "#2196f3" }}
                    ></i>
                    Selected Voice Info:
                  </div>
                  <div className="small">
                    <div>
                      <strong>Language:</strong> {selectedVoice.lang}
                    </div>
                    <div>
                      <strong>Name:</strong> {selectedVoice.name}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="mb-4">
              <label className="form-label fw-bold">
                <i className="bi bi-arrow-repeat me-2"></i>
                Repeat Count:{" "}
                <span style={{ color: "#2196f3" }}>{repetitionCount}</span>{" "}
                {repetitionCount === 1 ? "time" : "times"}
              </label>
              <div className="d-flex align-items-center gap-3">
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={repetitionCount}
                  onChange={(e) =>
                    _onChange("repetition_count", parseInt(e.target.value, 10))
                  }
                  className="form-range flex-grow-1"
                />
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={repetitionCount}
                  onChange={(e) =>
                    _onChange(
                      "repetition_count",
                      Math.max(1, parseInt(e.target.value, 10) || 1),
                    )
                  }
                  className="form-control"
                  style={{ width: "80px" }}
                />
              </div>
              <div className="form-text">
                <i className="bi bi-info-circle me-1"></i>
                The text will be spoken {repetitionCount}{" "}
                {repetitionCount === 1 ? "time" : "times"}
              </div>
            </div>

            {actionButtons()}

            {showSettings && (
              <VoiceConfiguration onChange={_onChange} settings={settingData} />
            )}
          </div>
        ) : (
          <div className="p-5">
            <Progress />
          </div>
        )}
      </div>
    </>
  );
}
